/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

/**
 * @file MeterIQ Limits Provider - New limits provider using MeterIQ service
 */

import { getSingletonFunction } from '../core/ProviderUtil';
import DcapiAPI from './DcapiAPI';
import { auth2 } from './Auth2API';
import type {
  Limit,
  VerbLimit,
  MeterIQLimitsResponse,
} from '../types/Limits';
import {
  OPERATION_GET_METERIQ_LIMITS,
  CONTENT_TYPE_METERIQ_LIMITS,
} from './UserAPI';

// Verb name mapping: MeterIQ API format -> Internal format
const METER_IQ_VERB_MAP = {
  expires_at: 'expires_at',
  uber: 'uber',
  createpdf: 'createpdf',
  wordtopdf: 'word-to-pdf',
  ppttopdf: 'ppt-to-pdf',
  exceltopdf: 'excel-to-pdf',
  jpgtopdf: 'jpg-to-pdf',
  ocrpdf: 'ocr-pdf',
  htmltopdf: 'html-to-pdf',
  organizepdf: 'organize-pdf',
  reorderpages: 'reorder-pages',
  deletepages: 'delete-pages',
  rotatepages: 'rotate-pages',
  insertpdf: 'insert-pdf',
  extractpages: 'extract-pages',
  exportpdf: 'export-pdf',
  pdftoimage: 'pdf-to-image',
  pdftoexcel: 'pdf-to-excel',
  pdftoppt: 'pdf-to-ppt',
  pdftoword: 'pdf-to-word',
  combinepdf: 'combine-pdf',
  protectpdf: 'protect-pdf',
  compresspdf: 'compress-pdf',
  splitpdf: 'split-pdf',
  editpdf: 'edit-pdf',
  croppages: 'crop-pages',
  numberpages: 'number-pages',
  highspeedscan: 'high-speed-scan',
};

// Reverse mapping: Internal format -> MeterIQ API format
// Built from METER_IQ_VERB_MAP for consistency
const REVERSE_METER_IQ_VERB_MAP = Object.entries(METER_IQ_VERB_MAP).reduce((acc, [key, value]) => {
  acc[value] = key;
  return acc;
}, {} as Record<string, string>);

const isVerbLimitEntry = (entry: unknown): entry is VerbLimit => (
  typeof entry === 'object'
  && entry !== null
  && 'limits' in entry
  && typeof (entry as VerbLimit).limits === 'object'
  && (entry as VerbLimit).limits !== null
);

/**
 * When a verb is consumed, also consume from shared group quotas.
 */
const consumeFromGroups = (
  limitsData: MeterIQLimitsResponse,
  actionVerb: string,
  /** When true (e.g. assets.download_uri), only touch group `download_limit`; otherwise group `limits`. */
  downloadQuota = false,
) => {
  const groups = (limitsData as { groups?: Record<string, unknown> }).groups;
  if (!groups || typeof groups !== 'object' || !actionVerb) {
    return;
  }
  const meterIQVerbKey = REVERSE_METER_IQ_VERB_MAP[actionVerb] || actionVerb;

  Object.values(groups).some(group => {
    const g = group as {
      verbs?: unknown[];
      download_limits?: { remaining?: number };
      limits?: { remaining?: number };
    };
    // Find group that matches the action verb
    const verbs = g?.verbs;
    if (!Array.isArray(verbs)) {
      return false;
    }
    const matches = verbs.some(v => v === meterIQVerbKey);
    if (!matches) {
      return false;
    }
    if (downloadQuota) {
      const dl = g.download_limits;
      if (dl && typeof dl.remaining === 'number' && dl.remaining > 0) {
        dl.remaining -= 1;
      }
      return true;
    }
    const lim = g.limits;
    if (lim && typeof lim.remaining === 'number' && lim.remaining > 0) {
      lim.remaining -= 1;
    }
    return true;
  });
};

const CACHE_KEY_PREFIX = 'meter_iq_limits';
export const UBER_LIMIT_EXCEEDED_ERROR_CODE = 'UberLimitExceeded';

/**
 * MeterIQ Limits Provider
 */
class MeterIQLimitsProvider {
  static getInstance = getSingletonFunction(MeterIQLimitsProvider);

  dcapiInstance = null;

  /**
   * Get DCAPI instance (cached)
   */
  getDcapi = async () => {
    if (this.dcapiInstance) {
      return this.dcapiInstance;
    }
    const dcapiPromise = await DcapiAPI.getInstance();
    const dcapiInstance = await dcapiPromise;
    this.dcapiInstance = dcapiInstance.getDcapi();
    return this.dcapiInstance;
  };

  shouldUseCache = (): boolean => {
    if (auth2.isSignedIn) {
      const userProfile = auth2.getUserProfile();
      return !!userProfile?.userId;
    }
    return true;
  }

  parseJwt(token) {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(window.atob(base64).split('').map(c => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`).join(''));
      return JSON.parse(jsonPayload);
    } catch (e) {
      return '';
    }
  }

  /**
   * Generate cache key based on surface and user
   * Format: meter_iq_limits/<surface>/<userId|anon>
   */
  getCacheKey = (): string => {
    const launchOptions = window.adobe_dc_sdk?.launchOptions || {};
    const surface = launchOptions.meteriq?.config?.surfaceIdentifier;

    let userId = 'anon';
    if (auth2.isSignedIn) {
      const userProfile = auth2.getUserProfile();
      userId = userProfile?.userId || 'anon';
    }

    return `${CACHE_KEY_PREFIX}/${surface}/${userId}`;
  };

  /**
   * Read limits from localStorage cache
   */
  readFromCache = () => {
    try {
      if (!this.shouldUseCache()) {
        return null;
      }
      const cacheKey = this.getCacheKey();
      const cachedData = window.adobe_dc_sdk.safeLocalStorage.getItem(cacheKey);

      if (!cachedData) {
        return null;
      }

      const parsed = JSON.parse(cachedData);
      return parsed;
    } catch (e) {
      return null;
    }
  };

  /**
   * Write limits to localStorage cache
   * Cache structure: { limits: {...} }
   */
  writeToCache = async (limitsData: MeterIQLimitsResponse): Promise<void> => {
    try {
      if (this.shouldUseCache()) {
        const cacheKey = this.getCacheKey();
        const cachePayload = {
          limits: limitsData,
          ...(!auth2.isSignedIn && { guestDeviceID: this.getGuestDeviceID() }),
        };

        window.adobe_dc_sdk.safeLocalStorage.setItem(cacheKey, JSON.stringify(cachePayload));
      }
    } catch (e) {
      // failure to write to cache
    }
  };

  /**
   * Check if cached data is still valid based on expires_at
   * expires_at values:
   * - -1: Cache for 1 year
   * - 0: No cache (always invalid)
   * - positive number: Unix timestamp in seconds
   */
  isCacheValid = async (cachedData): Promise<boolean> => {
    if (!cachedData || !cachedData.limits) {
      return false;
    }

    if (!auth2.isSignedIn) {
      const guestDeviceID = cachedData.guestDeviceID;
      const currentGuestDeviceID = this.getGuestDeviceID();
      if (guestDeviceID !== currentGuestDeviceID) {
        return false;
      }
    }

    const limitsData = cachedData.limits;
    const expiresAt = limitsData?.expires_at;

    if (expiresAt === undefined || expiresAt === null) {
      return false;
    }

    // expires_at = 0 means no cache
    if (expiresAt === 0) {
      return false;
    }

    // expires_at = -1 means cache for 1 year
    if (expiresAt === -1) {
      // For -1, we consider it valid for 1 year from now
      // Since we don't store timestamp anymore, we'll treat it as always valid
      // The cache will be invalidated on user state changes anyway
      return true;
    }

    // expires_at is a Unix timestamp (in seconds)
    const expiresAtMs = expiresAt * 1000;
    return Date.now() < expiresAtMs;
  };

  invalidateCache = () => {
    const cacheKey = this.getCacheKey();
    window.adobe_dc_sdk.safeLocalStorage.removeItem(cacheKey);
  };

  /**
   * Check if the uber limit is exhausted (remaining = 0)
   * Only checks 'uber' limit as it's the global limit that applies across all operations
   *
   * @param limits - Limits data from cache (cachedData.limits)
   * @returns boolean - true if uber limit is exhausted (0 remaining)
   */
  hasExhaustedLimits = (limits: MeterIQLimitsResponse): boolean => {
    if (!limits || !limits.uber) {
      return false;
    }

    // Check only the uber limit (global limit across all verbs)
    const uberLimit = limits.uber as VerbLimit;
    return uberLimit && uberLimit.limits && uberLimit.limits.remaining === 0;
  };

  /**
   * Enhanced cache validation for signed-in users with exhausted limits
   *
   * @param cachedData - Cached payload: { limits }
   * @returns Promise<boolean> - true if cache is valid
   */
  isCacheValidWithExhaustedLimitsCheck = async (cachedData): Promise<boolean> => {
    // 1. Basic expiry check (existing logic)
    const basicValidityCheck = await this.isCacheValid(cachedData);

    if (!basicValidityCheck) {
      return false;
    }

    // Additional check: If signed-in user AND has exhausted limits
    if (auth2.isSignedIn && this.hasExhaustedLimits(cachedData.limits)) {
      return false; // MeterIQ limits API call will be made once per session for this case on App load
    }

    return true;
  };

  getGuestDeviceID = () => {
    const currentAccessToken = auth2.getAccessTokenInfo({ isGuestAllowed: true }).access_token;
    const currentDecodedToken = this.parseJwt(currentAccessToken);
    return currentDecodedToken?.gdi ?? '';
  };

  readFromCacheWithValidation = async (): Promise<any | null> => {
    try {
      const cachedData = this.readFromCache();

      if (!cachedData) {
        return null;
      }

      // Use enhanced validation
      const isValid = await this.isCacheValidWithExhaustedLimitsCheck(cachedData);

      if (!isValid) {
        this.invalidateCache();
        return null;
      }

      return cachedData.limits;
    } catch (e) {
      return null; // invalid cache, return null
    }
  };

  /**
   * Fetch limits from MeterIQ API
   */
  fetchLimitsFromServer = async (): Promise<MeterIQLimitsResponse> => {
    const launchOptions = window.adobe_dc_sdk?.launchOptions || {};
    const { headers } = launchOptions.meteriq?.config || {};

    const dcapi = await this.getDcapi();
    const limits = await dcapi.call(OPERATION_GET_METERIQ_LIMITS, {
      headers: { ...headers }, // 'x-request-info' - client is expected to provide this header for different metering requirements
      accept: CONTENT_TYPE_METERIQ_LIMITS,
    });

    const { content: limitsData } = limits;

    return limitsData as MeterIQLimitsResponse;
  };

  /**
   * Transform MeterIQ API response to internal format
   */
  transformMeterIQLimits = (limits: MeterIQLimitsResponse): Record<string, VerbLimit | number> => {
    const transformedLimits: Record<string, VerbLimit | number> = {};

    Object.keys(limits).forEach(limit => {
      const mappedName = METER_IQ_VERB_MAP[limit] || limit;
      const entry = limits[limit];

      if (!isVerbLimitEntry(entry)) {
        transformedLimits[mappedName] = entry as VerbLimit | number;
        return;
      }

      const dl = (entry as unknown as { download_limits?: { remaining?: number } }).download_limits;
      const innerLimits: Limit = { ...entry.limits };
      if (typeof dl?.remaining === 'number') {
        innerLimits.download_remaining = dl.remaining;
      }

      transformedLimits[mappedName] = {
        limits: innerLimits,
        ...(entry.configuration !== undefined && { configuration: entry.configuration }),
      };
    });

    return transformedLimits;
  };

  /**
   * Sync limits from server or cache
   * For signed-in users with exhausted limits
   */
  syncServerLimits = async (useCache: boolean) => {
    if (!useCache) {
      // Remove the stale cache entry synchronously before the async fetch.
      // If the fetch is interrupted mid-flight (e.g. page navigation or context
      // teardown before writeToCache completes), the cache is already gone so the
      // next load always hits the server rather than reading stale data.
      this.invalidateCache();
    }

    let newLimitsData;
    let transformedData;
    // Try to read from cache first if useCache is true
    if (useCache) {
      newLimitsData = await this.readFromCacheWithValidation();

      if (newLimitsData) {
        // Resolve promise with cached transformed data (flattened with internal verb names)
        transformedData = this.transformMeterIQLimits(newLimitsData) as Record<string, VerbLimit | number>;
      }
    }

    // If no valid cache, fetch from server
    if (!newLimitsData) {
      newLimitsData = await this.fetchLimitsFromServer();

      // Resolve promise with transformed data (flattened with internal verb names)
      transformedData = this.transformMeterIQLimits(newLimitsData) as Record<string, VerbLimit | number>;

      await this.writeToCache(newLimitsData);
    }

    return transformedData;
  };

  /**
   * Get limits for the given user.
   *
   * Primary caller is UserAPI.getAndAddMeterIQResponseToUser(), which is protected
   * by UserAPI's userPromise deduplication. However, Limits2Provider.handleError also
   * calls this directly (bypassing UserAPI) on 429 errors, so concurrent force-fetch
   * calls are possible. The current concurrency is benign (two fetches write the same
   * data); a pendingFetch guard is a future improvement.
   *
   * @param useCache - Whether to use cached limits (if valid). Passing false also
   *                   synchronously invalidates the existing cache entry before fetching.
   * @returns Promise resolving to object with transformed limits data
   */
  getLimits = async (useCache: boolean = true) => {
    const limits = await this.syncServerLimits(useCache);
    return limits;
  }

  /**
   * Apply optimistic local usage to the MeterIQ cache, avoiding a server round-trip per operation.
   * If the verb belongs to a group, the group quota is updated instead of the per-verb quota.
   *
   * @param consumedLimits - The limits object from Limits2Provider after local consumption
   */
  updateCacheFromLocalDecrements(
    consumedLimits: Record<string, { remaining?: number; download_remaining?: number }>,
    actionVerb?: string,
  ) {
    const cache = this.readFromCache();
    if (!cache?.limits) {
      return; // No cache to update
    }

    // Determine operation type from what was consumed — no explicit flag needed
    let isDownloadOp = false;

    // Update each verb in the cache using REVERSE_METER_IQ_VERB_MAP to convert internal -> MeterIQ format
    Object.keys(consumedLimits).forEach(verbKey => {
      const meterIQKey = REVERSE_METER_IQ_VERB_MAP[verbKey] || verbKey;
      const verbEntry = cache.limits[meterIQKey] as { limits?: { remaining?: number }; download_limits?: { remaining?: number } };
      if (!verbEntry) {
        return;
      }
      const { download_remaining: nextDownloadRemaining, remaining: nextRemaining } = consumedLimits[verbKey];
      if (typeof nextDownloadRemaining === 'number') {
        isDownloadOp = true;
        if (verbEntry.download_limits) {
          verbEntry.download_limits.remaining = nextDownloadRemaining;
        }
      } else if (nextRemaining !== undefined && verbEntry.limits) {
        verbEntry.limits.remaining = nextRemaining;
      }
    });

    if (actionVerb) {
      consumeFromGroups(cache.limits, actionVerb, isDownloadOp);
    }

    // Write updated cache back to localStorage using existing writeToCache method
    this.writeToCache(cache.limits).catch(() => {
      // Ignore cache write errors
    });
  }

  /**
   * Get the remaining download quota for a verb.
   * Reads directly from the MeterIQ localStorage cache so it always reflects
   * optimistic decrements written by updateCacheFromLocalDecrements / consumeFromGroups.
   *
   * Group quota is used when available (it is the binding shared constraint);
   * falls back to the per-verb download_limits for ungrouped verbs.
   */
  getVerbDownloadRemaining = (verb: string): number => {
    const cache = this.readFromCache();
    if (!cache?.limits) return 0;

    const meterIQVerbKey = REVERSE_METER_IQ_VERB_MAP[verb] || verb;

    const groups = (cache.limits as { groups?: Record<string, unknown> }).groups;
    if (groups && typeof groups === 'object') {
      const groupEntry = Object.values(groups).find(g => {
        const group = g as { verbs?: unknown[] };
        return Array.isArray(group.verbs) && group.verbs.some(v => v === meterIQVerbKey);
      }) as { download_limits?: { remaining?: number } } | undefined;
      if (groupEntry) {
        return groupEntry.download_limits?.remaining ?? 0;
      }
    }

    return (cache.limits[meterIQVerbKey] as { download_limits?: { remaining?: number } })?.download_limits?.remaining ?? 0;
  };

  /**
   * Initialize provider
   */
  async ready() {
    return Promise.resolve(this);
  }

  /**
   * Reset limits (invalidate cache and reload)
   */
  async reset(): Promise<void> {
    this.syncServerLimits(false).catch(() => {
      // Ignore errors - limits will be retried on next user fetch
    });
  }

  /**
   * Transform MeterIQ limits response to legacy limits/conversions format
   * Used for backward compatibility with code expecting the old user/self API format
   *
   * @param {Object} meterIQLimits - Transformed MeterIQ limits (already mapped to internal verb names, flattened format without 'limits' wrapper)
   * @returns {Object} - limits/conversions format with all 21 fields
   *
   * @example
   * const meterIQLimits = {
   *   'createpdf': { remaining: 50, configuration: { max_file_size: 1048576 } },
   *   'export-pdf': { remaining: -1, configuration: { max_file_size: 2097152 } },
   *   'expires_at': 1234567890 // This will be skipped
   * };
   * const conversions = transformMeterIQToConversions(meterIQLimits);
   * // Returns: { create_pdf_conversions: { remaining: 50 }, create_pdf_max_file_size: { limit: 1048576 }, ... }
   */
  transformMeterIQToConversions(meterIQLimits: object): object {
    // Default file size limits (in bytes)
    const DEFAULT_MAX_FILE_SIZE_100KB = 102400; // 100 KB
    const DEFAULT_MAX_FILE_SIZE_1MB = 1048576; // 1 MB
    const DEFAULT_MAX_FILE_SIZE_2MB = 2097152; // 2 MB
    const DEFAULT_MAX_NUMBER_FILES = 100;
    const DEFAULT_MAX_SPLIT_POINTS = 20;

    const conversions : any = {};

    // Helper to safely extract remaining count (skip non-verb fields like expires_at)
    const getRemaining = verb => {
      const limitValue = meterIQLimits[verb];
      // Skip if it's just a number (expires_at) or undefined
      if (typeof limitValue === 'number' || !limitValue) {
        return 0;
      }
      // Access nested limits.remaining structure
      return limitValue.limits?.remaining ?? 0;
    };

    // Helper to safely extract configuration value
    const getConfig = (verb, configKey, defaultValue) => {
      const limitValue = meterIQLimits[verb];
      // Skip if it's just a number (expires_at) or undefined
      if (typeof limitValue === 'number' || !limitValue) {
        return defaultValue;
      }
      return limitValue.configuration?.[configKey] ?? defaultValue;
    };

    // Create PDF
    conversions.create_pdf_conversions = {
      remaining: getRemaining('createpdf'),
    };
    conversions.create_pdf_max_file_size = {
      limit: getConfig('createpdf', 'max_file_size', DEFAULT_MAX_FILE_SIZE_100KB),
    };

    // Export PDF
    conversions.export_pdf_conversions = {
      remaining: getRemaining('export-pdf'),
    };
    conversions.export_pdf_max_file_size = {
      limit: getConfig('export-pdf', 'max_file_size', DEFAULT_MAX_FILE_SIZE_100KB),
    };

    // Combine PDF
    conversions.combine_pdf_conversions = {
      remaining: getRemaining('combine-pdf'),
    };
    conversions.combine_pdf_max_file_size = {
      limit: getConfig('combine-pdf', 'max_file_size', DEFAULT_MAX_FILE_SIZE_100KB),
    };
    conversions.combine_pdf_documents = {
      limit: getConfig('combine-pdf', 'max_number_files', DEFAULT_MAX_NUMBER_FILES),
    };

    // Organize PDF
    conversions.organize_pdf_conversions = {
      remaining: getRemaining('organize-pdf'),
    };
    conversions.organize_pdf_max_file_size = {
      limit: getConfig('organize-pdf', 'max_file_size', DEFAULT_MAX_FILE_SIZE_100KB),
    };
    conversions.organize_pdf_documents = {
      limit: getConfig('organize-pdf', 'max_number_files', DEFAULT_MAX_NUMBER_FILES),
    };

    // OCR PDF
    conversions.ocr_pdf_conversions = {
      remaining: getRemaining('ocr-pdf'),
    };
    conversions.ocr_pdf_max_file_size = {
      limit: getConfig('ocr-pdf', 'max_file_size', DEFAULT_MAX_FILE_SIZE_100KB),
    };

    // Password Encrypt (protect-pdf in MeterIQ)
    conversions.password_encrypt_ops = {
      remaining: getRemaining('protect-pdf'),
    };
    conversions.password_encrypt_max_file_size = {
      limit: getConfig('protect-pdf', 'max_file_size', DEFAULT_MAX_FILE_SIZE_100KB),
    };

    // Optimize (compress-pdf in MeterIQ)
    conversions.optimize_pdf_ops = {
      remaining: getRemaining('compress-pdf'),
    };
    conversions.optimize_pdf_max_file_size = {
      limit: getConfig('compress-pdf', 'max_file_size', DEFAULT_MAX_FILE_SIZE_2MB),
    };

    // Edit PDF
    conversions.edit_pdf_ops = {
      remaining: getRemaining('edit-pdf'),
    };
    conversions.edit_pdf_max_file_size = {
      limit: getConfig('edit-pdf', 'max_file_size', DEFAULT_MAX_FILE_SIZE_100KB),
    };

    // Split PDF
    conversions.split_pdf_conversions = {
      remaining: getRemaining('split-pdf'),
    };
    conversions.split_pdf_max_file_size = {
      limit: getConfig('split-pdf', 'max_file_size', DEFAULT_MAX_FILE_SIZE_1MB),
    };
    conversions.split_pdf_max_split_points = {
      limit: getConfig('split-pdf', 'max_split_points', DEFAULT_MAX_SPLIT_POINTS),
    };

    // IPP Create PDF (legacy field, not provided by MeterIQ)
    conversions.ipp_create_pdf_conversions = {
      remaining: 0,
    };

    return conversions;
  }
}

export default MeterIQLimitsProvider;
